<template>
    <div class="list-reporting" :class="[scrollbarClass, { 'no-scroll': !expandedSectionNav }]">
        <div class="reporting-refresh-bar">
            <button class="btn-refresh-reporting" :disabled="reportingRefreshing" @click="refreshReportingData">
                <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': reportingRefreshing }"></i>
            </button>
        </div>

        <!-- Chargement initial -->
        <div v-if="reportingRefreshing && !reportingMetrics" class="reporting-loading">
            <div class="reporting-spinner"></div>
            <span>Chargement du reporting…</span>
        </div>

        <template v-if="!reportingRefreshing && !reportingMetrics">
            <div class="reporting-loading">Aucune donnée disponible pour ce post.</div>
        </template>

        <template v-if="reportingMetrics">
            <div class="reporting-metrics">
                <div class="metric-card" v-for="item in reportingItems" :key="item.key">
                    <span class="metric-label">{{ item.label }}</span>
                    <span class="metric-value">{{ item.value }}</span>
                </div>
            </div>
            <div v-if="event && event.page && event.page.network === 'facebook' && fbReactionsChart" class="reporting-breakdown">
                <span class="metric-label">{{ $t('dash.reporting.pdf.listItem.reactions') }}</span>
                <div class="bd-row" v-for="item in fbReactionsChart" :key="item.key">
                    <button class="bd-dot" :class="{ inactive: !item.visible }" :style="{ background: item.color }" @click="toggleFbChart('react_' + item.key)"></button>
                    <span class="bd-label">{{ item.label }}</span>
                    <div class="bd-track">
                        <div class="bd-fill" :style="{ width: item.visible ? item.pct + '%' : '0%', background: item.color }"></div>
                    </div>
                    <span class="bd-val">{{ item.value }}</span>
                </div>
            </div>
            <div v-else-if="reportingReactions" class="reporting-reactions">
                <span class="metric-label">{{ $t('dash.reporting.pdf.listItem.reactions') }}</span>
                <div class="reaction-list">
                    <div class="reaction-item" v-for="r in reportingReactions" :key="r.key">
                        <span class="reaction-key">{{ r.key }}</span>
                        <span class="reaction-count">{{ r.count }}</span>
                    </div>
                </div>
            </div>
            <div v-if="fbClicksChart" class="reporting-breakdown">
                <span class="metric-label">{{ $t('dash.reporting.clic') }}</span>
                <div class="bd-row" v-for="item in fbClicksChart" :key="item.key">
                    <button class="bd-dot" :class="{ inactive: !item.visible }" :style="{ background: item.color }" @click="toggleFbChart('click_' + item.key)"></button>
                    <span class="bd-label">{{ item.label }}</span>
                    <div class="bd-track">
                        <div class="bd-fill" :style="{ width: item.visible ? item.pct + '%' : '0%', background: item.color }"></div>
                    </div>
                    <span class="bd-val">{{ item.value }}</span>
                </div>
            </div>
            <a v-if="rmPermalink" :href="rmPermalink" target="_blank" rel="noopener noreferrer" class="reporting-permalink">
                <i class="fa-solid fa-arrow-up-right-from-square"></i> {{ $t('dash.posts.drawer.view-post') }}
            </a>
        </template>

    </div>
</template>

<script>
    import accessibilityMixin from '@/components/mixins/Accessibility.vue'

    export default {
        mixins: [accessibilityMixin],
        props: {
            event: {
                type: Object,
                required: true,
            },
            expandedSectionNav: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                localReportingData: null,
                reportingRefreshing: false,
                fbChartToggles: {},
            }
        },

        created() {
            this.loadReporting()
        },

        watch: {
            'event.originalPost.id'() {
                this.localReportingData = null
                this.fbChartToggles = {}
                this.loadReporting()
            },
        },

        methods: {
            async loadReporting() {
                if (this.reportingRefreshing) return
                this.reportingRefreshing = true
                try {
                    const botId = this.event.originalPost._bots[0].id
                    const newsId = this.event.originalPost.id
                    const base = process.env.VUE_APP_API_HOST
                    const res = await this.$http.get(`${base}/posts/reporting?botId=${botId}&newsId=${newsId}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    })
                    const body = res.body || {}
                    if (body.reportingData) this.localReportingData = body.reportingData
                } catch(e) { /* keep existing data on error */ }
                finally { this.reportingRefreshing = false }
            },

            async refreshReportingData() {
                this.localReportingData = null
                await this.loadReporting()
            },

            toggleFbChart(key) {
                const visible = this.fbChartToggles[key] !== false
                this.$set(this.fbChartToggles, key, !visible)
            },
        },

        computed: {
            reportingMetrics() {
                const rd = this.localReportingData || (this.event && this.event.reportingData)
                if (!rd || !rd.length) return null
                const doc = rd[0]
                if (!doc) return null
                return doc.metrics || doc
            },

            rmPermalink() {
                const m = this.reportingMetrics
                if (!m) return null
                const url = m.permalink_url || m.share_url || m.permalink || null
                if (!url) return null
                return url.startsWith('http') ? url : 'https://' + url
            },

            reportingItems() {
                const m = this.reportingMetrics
                if (!m) return []
                const net = this.event && this.event.page && this.event.page.network
                const postType = this.event && this.event.type
                const isStory = postType === 'story'
                const isVideo = postType === 'video' || postType === 'reel'
                const items = []
                const add = (key, label, raw, fmt) => {
                    if (raw == null) return
                    items.push({ key, label, value: fmt ? fmt(raw) : raw })
                }
                const pct = v => v.toFixed(2) + '%'
                const reactTotal = () => {
                    if (m.post_reactions && m.post_reactions[0] && m.post_reactions[0].sum != null) return m.post_reactions[0].sum
                    if (m.post_reactions_sum != null) return m.post_reactions_sum
                    return m.like_count != null ? m.like_count : null
                }

                if (net === 'instagram') {
                    const igInsights = m.insights && m.insights.data ? m.insights.data : null
                    const ins = (...names) => {
                        if (!igInsights) return null
                        for (const name of names) {
                            const obj = igInsights.find(o => o.name === name)
                            if (!obj) continue
                            const v = obj.values && obj.values.length ? obj.values[0].value : (obj.value != null ? obj.value : null)
                            if (v != null) return v
                        }
                        return null
                    }
                    const igImpressions   = m.post_impressions    != null ? m.post_impressions    : ins('impressions', 'views', 'plays')
                    const igReach         = m.reach               != null ? m.reach               : ins('reach')
                    const igShares        = m.shares              != null ? m.shares              : (m.shares_count != null ? m.shares_count : ins('shares'))
                    const igSaved         = m.saved               != null ? m.saved               : ins('saved')
                    const igTotalInter    = m.total_interactions  != null ? m.total_interactions  : ins('total_interactions')
                    const igFollows       = m.follows             != null ? m.follows             : ins('follows')
                    const igProfileVisits = m.profile_visits      != null ? m.profile_visits      : ins('profile_visits')
                    const igReplies       = m.replies             != null ? m.replies             : ins('replies')
                    const igEngagRaw = m.engagement != null ? m.engagement : ins('engagement', 'total_interactions')
                    const igEngag = igEngagRaw != null ? igEngagRaw
                                  : (m.like_count != null || m.comments_count != null
                                     ? (m.like_count || 0) + (m.comments_count || 0) + (igSaved || 0) + (igShares || 0)
                                     : null)
                    const igReachEngag = m.reach_engagement != null ? m.reach_engagement
                                       : (igImpressions > 0 && igEngag != null ? 100 * igEngag / igImpressions : null)

                    if (isStory) {
                        add('post_impressions',   this.$t('dash.reporting.impressions'),       igImpressions)
                        add('replies',            this.$t('dash.reporting.replies'),           igReplies)
                        add('shares',             this.$t('dash.reporting.share'),             igShares)
                        add('total_interactions', this.$t('dash.reporting.total_interactions'), igTotalInter)
                        add('profile_visits',     this.$t('dash.reporting.profile_visits'),   igProfileVisits)
                        add('follows',            this.$t('dash.reporting.follows'),           igFollows)
                    } else if (isVideo) {
                        add('post_impressions', this.$t('dash.label.fb.video_views'),      igImpressions)
                        add('reach',            this.$t('dash.label.fb.impressions_reach'), igReach)
                        add('reactions',        this.$t('dash.reporting.reaction'),         reactTotal())
                        add('comments_count',   this.$t('dash.reporting.comment'),          m.comments_count)
                        add('shares',           this.$t('dash.reporting.share'),            igShares)
                    } else {
                        add('post_impressions', this.$t('dash.reporting.impressions'), igImpressions)
                        add('reactions',        this.$t('dash.reporting.reaction'),    reactTotal())
                        add('comments_count',   this.$t('dash.reporting.comment'),    m.comments_count)
                        add('shares',           this.$t('dash.reporting.share'),      igShares)
                    }
                    if (igReachEngag != null) items.push({ key: 'reach_engagement', label: this.$t('dash.reporting.pdf.listItem.engagement'), value: igReachEngag.toFixed(2) + '%' })
                    return items

                } else if (net === 'facebook') {
                    const fbInsData = m.insights && Array.isArray(m.insights.data) ? m.insights.data : null
                    const fbIns = insightName => {
                        if (!fbInsData) return null
                        const o = fbInsData.find(x => x.name === insightName)
                        return o && o.values && o.values.length ? o.values[0].value : null
                    }
                    const fbImpressions = m.post_impressions != null ? m.post_impressions
                        : (fbIns('post_media_view') != null ? fbIns('post_media_view') : fbIns('post_impressions'))
                    const fbReactSum = reactTotal() != null ? reactTotal()
                        : (() => {
                            const rv = fbIns('post_reactions_by_type_total')
                            if (rv && typeof rv === 'object') return Object.values(rv).reduce((a, b) => a + b, 0)
                            return m.reactions && m.reactions.summary ? m.reactions.summary.total_count : null
                        })()
                    const fbComments = m.comments_count != null ? m.comments_count
                        : (m.comments && m.comments.summary ? m.comments.summary.total_count : null)
                    const fbShares = m.shares_count != null ? m.shares_count
                        : (m.shares && m.shares.count != null ? m.shares.count : null)
                    const fbClicksBreakdown = m.total_clicks == null && m.link_clicks == null
                        ? (() => { const v = fbIns('post_clicks_by_type'); return v && typeof v === 'object' ? v : null })()
                        : null
                    const fbLinkClicks = m.link_clicks != null ? m.link_clicks
                        : (fbClicksBreakdown ? fbClicksBreakdown['link clicks'] || 0 : null)
                    const fbTotalClicks = m.total_clicks != null ? m.total_clicks
                        : (fbClicksBreakdown ? Object.values(fbClicksBreakdown).reduce((a, b) => a + b, 0) : null)
                    const fbEngagement = m.reach_engagement != null ? m.reach_engagement
                        : (fbImpressions > 0
                            ? parseFloat((100 * ((fbReactSum || 0) + (fbComments || 0) + (fbShares || 0) + (fbLinkClicks || 0)) / fbImpressions).toFixed(2))
                            : null)
                    const fmtViewTime = v => { const s = Math.round(v / 1000); const min = Math.floor(s / 60); const sec = s % 60; return min > 0 ? `${min}m ${sec}s` : `${sec}s` }

                    if (isVideo) {
                        const fbVV     = m.video_view_count         != null ? m.video_view_count         : fbIns('post_video_views')
                        const fbVVOrg  = m.video_view_count_organic  != null ? m.video_view_count_organic  : fbIns('post_video_views_organic')
                        const fbVVPaid = m.video_view_count_paid     != null ? m.video_view_count_paid     : fbIns('post_video_views_paid')
                        const fbVV15   = m.video_view_count_15s      != null ? m.video_view_count_15s      : fbIns('post_video_views_15s')
                        const fbVVSnd  = m.video_view_count_sound_on != null ? m.video_view_count_sound_on : fbIns('post_video_views_sound_on')
                        const fbVVTime = m.video_view_time           != null ? m.video_view_time           : fbIns('post_video_view_time')
                        add('video_view_count',          this.$t('dash.label.fb.video_views'),          fbVV)
                        add('video_view_count_organic',  this.$t('dash.label.fb.video_views_organic'),  fbVVOrg)
                        add('video_view_count_paid',     this.$t('dash.label.fb.video_views_paid'),     fbVVPaid)
                        add('video_view_count_15s',      this.$t('dash.reporting.drawer.video_15s'),    fbVV15)
                        add('video_view_count_sound_on', this.$t('dash.label.fb.video_views_sound_on'), fbVVSnd)
                        if (fbVVTime != null) add('video_view_time', this.$t('dash.reporting.video_view_time'), fbVVTime, fmtViewTime)
                        add('post_impressions', this.$t('dash.reporting.impressions'), fbImpressions)
                        add('reactions',        this.$t('dash.reporting.reaction'),    fbReactSum)
                        add('comments_count',   this.$t('dash.reporting.comment'),     fbComments)
                        add('shares_count',     this.$t('dash.reporting.share'),       fbShares)
                    } else {
                        add('post_impressions', this.$t('dash.reporting.impressions'), fbImpressions)
                        add('reactions',        this.$t('dash.reporting.reaction'),    fbReactSum)
                        add('comments_count',   this.$t('dash.reporting.comment'),     fbComments)
                        add('shares_count',     this.$t('dash.reporting.share'),       fbShares)
                        const fbClicks = fbTotalClicks != null ? fbTotalClicks : fbLinkClicks
                        add('total_clicks',     this.$t('dash.reporting.clic'),        fbClicks)
                        add('interactions',     this.$t('dash.label.fb.post_engagements'), m.interactions)
                    }
                    if (m.reach_engagement == null && fbEngagement != null) {
                        items.push({ key: 'reach_engagement', label: this.$t('dash.reporting.pdf.listItem.engagement'), value: fbEngagement.toFixed(2) + '%' })
                    }

                } else if (net === 'linkedin') {
                    add('post_impressions', this.$t('dash.reporting.impressions'), m.post_impressions)
                    add('reactions',        this.$t('dash.reporting.reaction'),   reactTotal())
                    add('comments_count',   this.$t('dash.reporting.comment'),    m.comments_count)
                    add('shares_count',     this.$t('dash.reporting.share'),      m.shares_count)
                    const liClicks = m.total_clicks != null ? m.total_clicks : (m.link_clicks != null ? m.link_clicks : (m.click_count != null ? m.click_count : null))
                    add('clicks',           this.$t('dash.reporting.clic'),       liClicks)

                } else if (net === 'tiktok') {
                    add('view_count',          this.$t('dash.label.fb.video_views'),         m.post_impressions != null ? m.post_impressions : m.view_count)
                    add('video_finish_rate',   this.$t('dash.reporting.drawer.completion_rate'), m.video_finish_rate, pct)
                    add('viewer',              this.$t('dash.reporting.drawer.viewers'),      m.viewer)
                    add('video_new_follower',  this.$t('dash.reporting.drawer.new_followers_video'), m.video_new_follower)
                    add('reactions',           this.$t('dash.reporting.reaction'),            reactTotal())
                    add('comments_count',      this.$t('dash.reporting.comment'),             m.comments_count != null ? m.comments_count : m.comment_count)
                    add('shares_count',        this.$t('dash.reporting.share'),               m.shares_count != null ? m.shares_count : m.share_count)

                } else {
                    add('post_impressions', this.$t('dash.reporting.impressions'), m.post_impressions != null ? m.post_impressions : m.view_count)
                    add('reactions',        this.$t('dash.reporting.reaction'),   reactTotal())
                    add('comments_count',   this.$t('dash.reporting.comment'),    m.comments_count)
                    add('shares_count',     this.$t('dash.reporting.share'),      m.shares_count)
                    const clicks = m.total_clicks != null ? m.total_clicks : (m.link_clicks != null ? m.link_clicks : null)
                    add('clicks',           this.$t('dash.reporting.clic'),       clicks)
                }

                add('reach_engagement', this.$t('dash.reporting.pdf.listItem.engagement'), m.reach_engagement, pct)

                return items
            },

            reportingReactions() {
                const m = this.reportingMetrics
                if (!m) return null
                if (m.post_reactions && m.post_reactions[0]) {
                    const rv = m.post_reactions[0].value
                    if (rv && typeof rv === 'object') {
                        const entries = Object.entries(rv).filter(([, v]) => v >= 0).map(([k, v]) => ({ key: k, count: v }))
                        return entries.length ? entries : null
                    }
                }
                if (m.insights && Array.isArray(m.insights.data)) {
                    const o = m.insights.data.find(x => x.name === 'post_reactions_by_type_total')
                    if (o && o.values && o.values.length) {
                        const rv = o.values[0].value
                        if (rv && typeof rv === 'object') {
                            const entries = Object.entries(rv).filter(([, v]) => v >= 0).map(([k, v]) => ({ key: k, count: v }))
                            return entries.length ? entries : null
                        }
                    }
                }
                return null
            },

            fbReactionsChart() {
                const reactions = this.reportingReactions
                if (!reactions || !reactions.length) return null
                const m = this.reportingMetrics
                const total = (m && m.post_reactions && m.post_reactions[0] && m.post_reactions[0].sum != null
                    ? m.post_reactions[0].sum
                    : reactions.reduce((a, r) => a + r.count, 0)) || 1
                const COLORS = { LIKE: '#1877F2', LOVE: '#F33E58', HAHA: '#F7B125', WOW: '#F7AD00', SAD: '#5B99E0', ANGRY: '#E9710F', CARE: '#F9A61A', THANKFUL: '#5B99E0', PRIDE: '#E42B4B' }
                return reactions.map(r => ({
                    key: r.key,
                    label: r.key.charAt(0) + r.key.slice(1).toLowerCase(),
                    value: r.count,
                    pct: total > 0 ? Math.round(100 * r.count / total) : 0,
                    color: COLORS[r.key] || '#9B59B6',
                    visible: this.fbChartToggles['react_' + r.key] !== false,
                }))
            },

            fbClicksChart() {
                const m = this.reportingMetrics
                if (!m) return null
                let bd = m.post_clicks_breakdown && typeof m.post_clicks_breakdown === 'object' ? m.post_clicks_breakdown : null
                if (!bd && m.insights && Array.isArray(m.insights.data)) {
                    const o = m.insights.data.find(x => x.name === 'post_clicks_by_type')
                    if (o && o.values && o.values.length && typeof o.values[0].value === 'object') bd = o.values[0].value
                }
                if (!bd) return null
                const total = Object.values(bd).reduce((a, b) => a + b, 0) || 1
                const COLORS = ['#6B1F4D', '#9B59B6', '#1877F2', '#E9710F', '#42B72A', '#F7B125']
                const entries = Object.entries(bd)
                if (!entries.length) return null
                return entries.map(([k, v], i) => ({
                    key: k,
                    label: k.charAt(0).toUpperCase() + k.slice(1),
                    value: v,
                    pct: total > 0 ? Math.round(100 * v / total) : 0,
                    color: COLORS[i % COLORS.length],
                    visible: this.fbChartToggles['click_' + k] !== false,
                }))
            },
        },
    }
</script>

<style lang="scss" scoped>
.list-reporting {
    padding: 14px 12px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.reporting-loading {
    font-size: 12px;
    color: #b08aa0;
    text-align: center;
    padding: 32px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}
.reporting-spinner {
    width: 22px;
    height: 22px;
    border: 3px solid #e8dae5;
    border-top-color: #6B1F4D;
    border-radius: 50%;
    animation: spin-reporting 0.75s linear infinite;
}
@keyframes spin-reporting {
    to { transform: rotate(360deg); }
}
.reporting-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}
.metric-card {
    background: #faf7fc;
    border: 1px solid #ede6f3;
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.metric-label {
    font-size: 11px;
    color: #b08aa0;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.metric-value {
    font-size: 20px;
    font-weight: 700;
    color: #6B1F4D;
}
.reporting-reactions {
    background: #faf7fc;
    border: 1px solid #ede6f3;
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.reaction-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 2px;
}
.reaction-item {
    display: flex;
    align-items: center;
    gap: 4px;
    background: #fff;
    border: 1px solid #ede6f3;
    border-radius: 20px;
    padding: 2px 9px 2px 7px;
    font-size: 12px;
}
.reaction-key {
    color: #6B1F4D;
    font-weight: 600;
    text-transform: capitalize;
}
.reaction-count {
    color: #444;
}
.reporting-permalink {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: #6B1F4D;
    text-decoration: none;
    font-weight: 500;
    padding: 6px 0;
}
.reporting-permalink:hover {
    text-decoration: underline;
}
.reporting-refresh-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: -4px;
}
.btn-refresh-reporting {
    background: none;
    border: none;
    cursor: pointer;
    color: #888;
    font-size: 13px;
    padding: 4px 6px;
    border-radius: 4px;
    transition: color 0.15s, background 0.15s;
}
.btn-refresh-reporting:hover:not(:disabled) {
    color: #333;
    background: #f0f0f0;
}
.btn-refresh-reporting:disabled {
    opacity: 0.5;
    cursor: default;
}
.reporting-breakdown {
    background: #faf7fc;
    border: 1px solid #ede6f3;
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.bd-row {
    display: flex;
    align-items: center;
    gap: 7px;
}
.bd-dot {
    flex-shrink: 0;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: opacity 0.15s;
}
.bd-dot.inactive {
    opacity: 0.25;
}
.bd-label {
    flex-shrink: 0;
    width: 82px;
    font-size: 11px;
    color: #777;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.bd-track {
    flex: 1;
    height: 6px;
    background: #ede6f3;
    border-radius: 3px;
    overflow: hidden;
}
.bd-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s ease;
}
.bd-val {
    flex-shrink: 0;
    width: 32px;
    text-align: right;
    font-size: 12px;
    font-weight: 600;
    color: #6B1F4D;
}
</style>
